import { Component, EventEmitter, Input, OnInit, Output, ViewChild, OnChanges, SimpleChanges, TemplateRef } from '@angular/core';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { L10N_LOCALE, L10nLocale } from 'angular-l10n';
import { Inject } from '@angular/core';
import { ActiveFilter, ColumnConfig } from 'src/utilities/interfaces/generic-list.interfaces';


@Component({
  selector: 'app-generic-list',
  templateUrl: './generic-list.component.html',
  styleUrls: ['./generic-list.component.scss']
})
export class GenericListComponent implements OnInit, OnChanges {
  @Input() items: any[] = []
  @Input() columns: ColumnConfig[] = []
  @Input() enablePagination = true
  @Input() serverSidePagination = false // Toggle entre paginación local y server-side
  @Input() totalItems = 0
  @Input() pageSize = 10
  @Input() pageIndex = 0
  @Input() pageSizeOptions: number[] = [10, 15, 30, 45]
  @Input() showFilters = false
  @Input() loading = false
  @Input() activeFilters: ActiveFilter[] = []
  @Input() noDataMessage = '$general.txtNoData'
  @Input() loadingMessage = '$general.loading'
  @Input() showResultsCount = true
  @Input() resultsLabel = '$general.txtResults'
  @Input() clickableRows = true
  @Input() hoverEffect = true

  @Output() onRowClick = new EventEmitter<any>()
  @Output() onPageChange = new EventEmitter<PageEvent>()
  @Output() onFilterClear = new EventEmitter<string>()
  @Output() onSort = new EventEmitter<{ column: string; direction: 'asc' | 'desc' }>()

  @ViewChild(MatPaginator) paginator: MatPaginator

  processedItems: any[] = []
  currentPageIndex = 0
  currentPageSize = 10
  sortColumn = ""
  sortDirection: "asc" | "desc" = "asc"
  filteredItemsCount = 0

  constructor(@Inject(L10N_LOCALE) public locale: L10nLocale) {}

  ngOnInit(): void {
    this.currentPageIndex = this.pageIndex
    this.currentPageSize = this.pageSize
    this.processData()
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["items"]) {
      if (this.serverSidePagination) {
        // En modo server-side, solo mostramos los items recibidos
        this.processedItems = this.items
        this.filteredItemsCount = this.totalItems
      } else {
        // En modo local, procesamos los datos
        if (changes["items"].firstChange) {
          this.currentPageIndex = 0
        }
        this.processData()
      }
    }

    if (changes["activeFilters"] && !this.serverSidePagination) {
      this.currentPageIndex = 0
      this.processData()
    }

    if (changes["totalItems"] && this.serverSidePagination) {
      this.filteredItemsCount = this.totalItems
    }

    if (changes["pageIndex"]) {
      this.currentPageIndex = this.pageIndex
    }

    if (changes["pageSize"]) {
      this.currentPageSize = this.pageSize
    }
  }

  private processData(): void {
    // Si es server-side, no procesamos aquí
    if (this.serverSidePagination) {
      this.processedItems = this.items
      this.filteredItemsCount = this.totalItems
      return
    }

    // Paginación local
    let filtered = [...this.items]

    // Aplicar filtros activos
    if (this.activeFilters && this.activeFilters.length > 0) {
      this.activeFilters.forEach((filter) => {
        filtered = filtered.filter((item) => {
          const value = this.getValue(item, filter.parameter)
          if (value === null || value === undefined) return false
          return value.toString().toLowerCase().includes(filter.value.toString().toLowerCase())
        })
      })
    }

    this.filteredItemsCount = filtered.length

    // Aplicar ordenamiento
    if (this.sortColumn) {
      filtered.sort((a, b) => {
        const valueA = this.getValue(a, this.sortColumn)
        const valueB = this.getValue(b, this.sortColumn)

        // Manejo de valores null/undefined
        if (valueA === null || valueA === undefined) return 1
        if (valueB === null || valueB === undefined) return -1

        // Comparación numérica si ambos son números
        if (typeof valueA === 'number' && typeof valueB === 'number') {
          return this.sortDirection === "asc" ? valueA - valueB : valueB - valueA
        }

        // Comparación de strings (case insensitive)
        const strA = valueA.toString().toLowerCase()
        const strB = valueB.toString().toLowerCase()

        if (strA < strB) return this.sortDirection === "asc" ? -1 : 1
        if (strA > strB) return this.sortDirection === "asc" ? 1 : -1
        return 0
      })
    }

    // Aplicar paginación local
    if (this.enablePagination) {
      const start = this.currentPageIndex * this.currentPageSize
      const end = start + this.currentPageSize
      this.processedItems = filtered.slice(start, end)
    } else {
      this.processedItems = filtered
    }
  }

  /**
   * Obtiene el valor de una propiedad anidada usando notación de punto
   */
  getValue(obj: any, path: string): any {
    if (!path || !obj) return null
    return path.split(".").reduce((current, part) => current?.[part], obj)
  }

  /**
   * Formatea el valor de una columna según su configuración
   */
  getFormattedValue(item: any, column: ColumnConfig): string {
    const value = this.getValue(item, column.field)

    // Si tiene pipe personalizado, usarlo
    if (column.pipe) {
      return column.pipe(value, item)
    }

    if (value === null || value === undefined) return "-"

    switch (column.format) {
      case "date":
        try {
          return new Date(value).toLocaleDateString(this.locale.language)
        } catch {
          return value.toString()
        }

      case "datetime":
        try {
          return new Date(value).toLocaleString(this.locale.language)
        } catch {
          return value.toString()
        }

      case "currency":
        return new Intl.NumberFormat(this.locale.language, {
          style: "currency",
          currency: "EUR",
        }).format(Number(value))

      case "number":
        return new Intl.NumberFormat(this.locale.language).format(Number(value))

      case "boolean":
        return value ? "Sí" : "No"

      case "badge":
        return value?.toString() || "-"

      default:
        return value?.toString() || "-"
    }
  }

  /**
   * Obtiene la clase CSS para badges dinámicos
   */
  getBadgeClass(item: any, column: ColumnConfig): string {
    if (column.format !== 'badge' || !column.badgeClass) {
      return ''
    }
    const value = this.getValue(item, column.field)
    return column.badgeClass(value, item)
  }

  /**
   * Verifica si una columna puede ordenarse
   */
  isSortable(column: ColumnConfig): boolean {
    return column.sortable !== false && column.format !== 'custom'
  }

  /**
   * Maneja el click en una fila
   */
  handleRowClick(item: any): void {
    if (this.clickableRows) {
      this.onRowClick.emit(item)
    }
  }

  /**
   * Alterna el ordenamiento de una columna
   */
  toggleSort(column: ColumnConfig): void {
    if (!this.isSortable(column)) return

    const columnField = column.field

    if (this.serverSidePagination) {
      // En modo server-side, emitir evento para que el padre maneje el ordenamiento
      if (this.sortColumn === columnField) {
        this.sortDirection = this.sortDirection === "asc" ? "desc" : "asc"
      } else {
        this.sortColumn = columnField
        this.sortDirection = "asc"
      }
      this.onSort.emit({ column: columnField, direction: this.sortDirection })
    } else {
      // En modo local, ordenar aquí
      if (this.sortColumn === columnField) {
        this.sortDirection = this.sortDirection === "asc" ? "desc" : "asc"
      } else {
        this.sortColumn = columnField
        this.sortDirection = "asc"
      }
      this.currentPageIndex = 0
      this.processData()
    }
  }

  /**
   * Maneja cambios de página
   */
  handlePageChange(event: PageEvent): void {
    this.currentPageIndex = event.pageIndex
    this.currentPageSize = event.pageSize

    if (this.serverSidePagination) {
      // En modo server-side, emitir evento para que el padre cargue los datos
      this.onPageChange.emit(event)
    } else {
      // En modo local, procesar datos aquí
      this.processData()
      this.onPageChange.emit(event)
    }
  }

  /**
   * Limpia un filtro específico
   */
  clearFilter(parameter: string): void {
    this.onFilterClear.emit(parameter)
  }

  /**
   * Obtiene el icono de ordenamiento para una columna
   */
  getSortIcon(column: ColumnConfig): string {
    if (!this.isSortable(column)) return ''
    if (this.sortColumn !== column.field) return 'fa-sort'
    return this.sortDirection === 'asc' ? 'fa-sort-up' : 'fa-sort-down'
  }

  /**
   * Obtiene la alineación de texto para una columna
   */
  getTextAlign(column: ColumnConfig): string {
    return column.align || 'left'
  }
}
